import React from 'react';
import { Link } from 'react-router-dom';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';

const COMMERCIAL_CONCRETE_FAQ = [
  {
    question: 'How often should commercial concrete be cleaned?',
    answer: 'We recommend cleaning high-traffic commercial concrete surfaces at least twice a year to maintain safety and appearance. Areas with heavy use or exposure to oil and grease may require more frequent cleaning.'
  },
  {
    question: 'Can you remove oil stains and gum from sidewalks and parking lots?',
    answer: 'Yes! We use professional-grade degreasers and hot water pressure washing to remove oil stains, gum, and other tough contaminants from commercial concrete surfaces.'
  },
  {
    question: 'Is your cleaning process safe for all types of commercial concrete?',
    answer: {
      intro: 'Absolutely. We tailor our cleaning methods to the specific needs of your property, including:',
      list: [
        'Sidewalks',
        'Parking lots',
        'Loading docks',
        'Dumpster pads',
        'Entryways'
      ],
      outro: 'Our technicians use the right pressure and solutions to ensure a thorough clean without damaging your surfaces.'
    }
  }
];

const CommercialConcreteCleaning = () => {
  const serviceSEO = {
    title: 'Commercial Concrete Cleaning | Copper Canyon Pressure Wash',
    description: 'Professional commercial concrete cleaning in Phoenix, AZ. Remove stains, dirt, and grime from sidewalks, parking lots, and more. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Commercial Concrete Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Commercial Concrete Cleaning'
        }
      }
    }
  };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Commercial Concrete Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional commercial concrete cleaning in Phoenix, AZ. Remove oil stains & grime. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="warehouse floor cleaning, parking garage cleaning, Phoenix concrete maintenance, industrial floor cleaning, concrete stain removal, commercial surface cleaning, concrete restoration, concrete safety cleaning, concrete surface preparation, commercial property maintenance"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Service Banner">
        <picture>
          <source srcSet="/concrete.avif" type="image/avif" />
          <source srcSet="/concrete.webp" type="image/webp" />
        <img 
          src="/concrete.JPG" 
          alt="Professional commercial concrete cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Commercial Concrete Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Commercial Concrete Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          Our team provides expert commercial concrete cleaning for sidewalks, parking lots, dumpster pads, and more. We use advanced equipment and eco-friendly solutions to keep your property safe, clean, and welcoming.
        </p>
        <h3 className="text-xl font-semibold text-[#4a3f35] mb-2">Why Choose Us?</h3>
        <ul className="list-disc pl-6 mb-6 text-gray-700">
          <li>Experienced, professional technicians</li>
          <li>Eco-friendly cleaning solutions</li>
          <li>Advanced equipment and techniques</li>
          <li>Flexible scheduling for businesses</li>
          <li>100% satisfaction guarantee</li>
        </ul>
      </div>
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Commercial Concrete Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our commercial concrete cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ items={COMMERCIAL_CONCRETE_FAQ} title="Frequently Asked Questions About Commercial Concrete Cleaning" />
    </div>
  );
};

export default CommercialConcreteCleaning; 